import { useCallback } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import {
  PRESIGNED_URL_REFETCH_INTERVAL,
  PRESIGNED_URL_STALE_TIME,
} from '@/hooks/opportunity/presignedFileUrls'
import {
  buildOpportunityDetailQueryString,
  type OpportunityDetailFetchOptions,
} from '@/hooks/opportunity/queryParams'

import type {
  FederalGrant,
  FederalGrantNotice,
} from '@/types/federal-grant/FederalGrant'
import type { UUID } from '@/types/Id'
import type { SearchOut } from '@/types/Search'

export const fetchFederalGrant = async (
  federalGrantId: string,
  options?: OpportunityDetailFetchOptions,
): Promise<FederalGrant | null> => {
  if (federalGrantId === '') {
    return null
  }
  const params = buildOpportunityDetailQueryString(options)
  const url = `${API_URL}/api/v1/federal-grants/${federalGrantId}${params}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const federalGrantWithoutPresignedUrlsQueryKey = (
  federalGrantId: string,
) => [federalGrantId, 'federalGrant', 'withoutPresignedUrls'] as const

export const fetchFederalGrantWithoutPresignedUrls = (federalGrantId: string) =>
  fetchFederalGrant(federalGrantId, { includePresignedUrls: false })

export const useSuspenseFederalGrantWithoutPresignedUrls = (
  federalGrantId: string,
) => {
  return useSuspenseQuery({
    queryKey: federalGrantWithoutPresignedUrlsQueryKey(federalGrantId),
    queryFn: async () => {
      const data = await fetchFederalGrantWithoutPresignedUrls(federalGrantId)
      if (!data) throw new Response(null, { status: 404 })
      return data
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const federalGrantPresignedUrlsQueryKey = (federalGrantId: string) =>
  [federalGrantId, 'federalGrant', 'presignedUrls'] as const

// Live client-side fetch with presigned file URLs; the page shows it in place of the ISR copy.
export const useFederalGrantPresignedUrls = (federalGrantId: string) => {
  return useQuery({
    queryKey: federalGrantPresignedUrlsQueryKey(federalGrantId),
    queryFn: () => fetchFederalGrant(federalGrantId),
    staleTime: PRESIGNED_URL_STALE_TIME,
    refetchInterval: PRESIGNED_URL_REFETCH_INTERVAL,
    enabled: federalGrantId !== '',
  })
}

export const useFederalGrant = (
  federalGrantId: string,
  initialData?: FederalGrant,
) => {
  return useQuery({
    queryKey: [federalGrantId, 'federalGrant'],
    queryFn: () => fetchFederalGrant(federalGrantId),
    initialData: initialData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalGrantId !== '',
  })
}

export const useFederalGrantLatestNotice = (federalGrantId: UUID | '') => {
  const selectLatestNotice = useCallback(
    (data: FederalGrant | null): FederalGrantNotice | null => {
      if (!data || !data.notices || data.notices.length === 0) {
        return null
      }

      // Sort notices by post_date in descending order and get the first one
      const latestNotice = [...data.notices].sort(
        (a, b) =>
          new Date(b.post_date).getTime() - new Date(a.post_date).getTime(),
      )[0]

      return latestNotice
    },
    [],
  )

  return useQuery({
    queryKey: [federalGrantId, 'federalGrant'],
    queryFn: () => fetchFederalGrant(federalGrantId),
    select: selectLatestNotice,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalGrantId !== '',
  })
}

export const fetchSimilarFederalGrants = async (
  federalGrantId: string,
): Promise<Array<SearchOut>> => {
  if (federalGrantId === '') {
    return []
  }
  const url = `${API_URL}/api/v1/federal-grants/${federalGrantId}/similar`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const useSimilarFederalGrants = (federalGrantId: string) => {
  return useQuery({
    queryKey: [federalGrantId, 'similarFederalGrants'],
    queryFn: () => fetchSimilarFederalGrants(federalGrantId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalGrantId !== '',
  })
}
